import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTourContext } from '../../contexts/TourContext';

// Workaround for framer-motion type issue with React 18
const SafeAnimatePresence = AnimatePresence as any;

const TourOverlay: React.FC = () => {
    const { isActive, currentStep, nextStep } = useTourContext();
    const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);

    useEffect(() => {
        if (!isActive || !currentStep) {
            setHighlightRect(null);
            return;
        }

        const updateHighlight = () => {
            const targetElement = document.querySelector(currentStep.targetSelector) as HTMLElement;

            if (targetElement) {
                const rect = targetElement.getBoundingClientRect();
                setHighlightRect(rect);
                // Não precisa mais manipular z-index - frame approach deixa elemento livre
            }
        };

        updateHighlight();

        // Update on window resize and scroll
        window.addEventListener('resize', updateHighlight);
        window.addEventListener('scroll', updateHighlight, true);

        return () => {
            window.removeEventListener('resize', updateHighlight);
            window.removeEventListener('scroll', updateHighlight, true);

            // Não precisa mais limpar z-index
        };
    }, [isActive, currentStep]);

    // Listen for clicks on the highlighted element
    useEffect(() => {
        if (!currentStep?.requireAction || !isActive) return;

        const handleElementClick = (e: Event) => {
            console.log('[TourOverlay] Element clicked!', currentStep.id);

            if (currentStep.preventDefaultAction) {
                e.preventDefault();
                e.stopPropagation();
            }

            // Auto-advance tour
            setTimeout(() => nextStep(), 100);
        };

        const targetElement = document.querySelector(currentStep.targetSelector);
        if (targetElement) {
            targetElement.addEventListener('click', handleElementClick, { capture: true });

            return () => {
                targetElement.removeEventListener('click', handleElementClick, { capture: true });
            };
        }
    }, [currentStep, isActive, nextStep]);

    // Se o tour não está ativo, não renderiza nada
    if (!isActive) {
        return null;
    }

    return (
        <SafeAnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 z-[9998]"
                style={{ pointerEvents: 'none' }}  // Container não bloqueia
            >
                {highlightRect ? (
                    <>
                        {/* FRAME-BASED BLOCKING: 4 barras ao redor do spotlight */}

                        {/* Top Bar - do topo até o spotlight */}
                        <div
                            className="absolute left-0 right-0 bg-black/50 dark:bg-black/70"
                            style={{
                                top: 0,
                                height: `${highlightRect.top - 8}px`,
                                pointerEvents: 'auto',  // Bloqueia cliques
                            }}
                        />

                        {/* Bottom Bar - do spotlight até o fundo */}
                        <div
                            className="absolute left-0 right-0 bg-black/50 dark:bg-black/70"
                            style={{
                                top: `${highlightRect.bottom + 8}px`,
                                bottom: 0,
                                pointerEvents: 'auto',  // Bloqueia cliques
                            }}
                        />

                        {/* Left Bar - lateral esquerda */}
                        <div
                            className="absolute bg-black/50 dark:bg-black/70"
                            style={{
                                left: 0,
                                top: `${highlightRect.top - 8}px`,
                                width: `${highlightRect.left - 8}px`,
                                height: `${highlightRect.height + 16}px`,
                                pointerEvents: 'auto',  // Bloqueia cliques
                            }}
                        />

                        {/* Right Bar - lateral direita */}
                        <div
                            className="absolute bg-black/50 dark:bg-black/70"
                            style={{
                                left: `${highlightRect.right + 8}px`,
                                top: `${highlightRect.top - 8}px`,
                                right: 0,
                                height: `${highlightRect.height + 16}px`,
                                pointerEvents: 'auto',  // Bloqueia cliques
                            }}
                        />

                        {/* Spotlight Border - Visual apenas, não bloqueia */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.3, type: 'spring', stiffness: 200 }}
                            className="absolute"
                            style={{
                                top: `${highlightRect.top - 8}px`,
                                left: `${highlightRect.left - 8}px`,
                                width: `${highlightRect.width + 16}px`,
                                height: `${highlightRect.height + 16}px`,
                                borderRadius: '8px',
                                pointerEvents: 'none',  // Nunca bloqueia
                            }}
                        >
                            {/* Borda animada */}
                            <div className={`absolute inset-0 rounded-lg border-2 border-primary dark:border-primary-darkMode ${currentStep?.requireAction ? 'animate-pulse' : ''}`} />
                        </motion.div>
                    </>
                ) : (
                    // Fallback: overlay completo para steps sem highlight
                    <div className="absolute inset-0 bg-black/50 dark:bg-black/70" style={{ pointerEvents: 'auto' }} />
                )}
            </motion.div>
        </SafeAnimatePresence>
    );
};

export default TourOverlay;
